import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { HtMenuService } from '../lib-shared/services/htmenu.service';
import { AuthorizeService } from '../lib-shared/services/authorize.service';
import { UserService } from '../lib-shared/services/user.service';
import { PermissionTypes } from '../lib-shared/vs-constants';

// Bản sao giống hệt ở WorkClient/src/app/services/menu-group.service.ts — sửa thì sửa cả hai.

export const MA_NHOM_KHAC = 'OTHER';

export interface MenuNhom {
    ma: string;
    ten: string;
    icon: string;
    mauSac: string;
    thuTu: number;
    phanQuyens: string[];
}

export interface MenuPhanHe {
    phanHe: number;
    tenPhanHe: string;
    // Mở app này (rỗng = chưa cấu hình URL trong environment)
    url: string;
    laAppHienTai: boolean;
    // Mọi mã nhóm đang hoạt động của phân hệ, kể cả nhóm user không có quyền — để xếp menu vào nhóm
    maNhoms: string[];
    // Nhóm user có quyền ít nhất một menu
    nhoms: MenuNhom[];
}

@Injectable({
    providedIn: 'root'
})
export class MenuGroupService {

    // null = chưa tải xong; [] = không có cấu hình nhóm → sidebar hiện đầy đủ như cũ
    readonly phanHes$ = new BehaviorSubject<MenuPhanHe[]>(null);

    // Nhóm đang chọn trên sidebar của app hiện tại
    readonly selectedCode$ = new BehaviorSubject<string>(null);

    private readonly _idPhanHe: number = environment.clientDomain.idPhanhe;
    private readonly _selectedKey = 'htMenuGroup' + environment.clientDomain.idPhanhe;
    private readonly _requestKey = 'htMenuGroupRequest' + environment.clientDomain.idPhanhe;

    constructor(
        private _htMenuService: HtMenuService,
        private _authorizeService: AuthorizeService,
        private _userService: UserService
    ) { }

    /**
     * Gọi ở constructor AppComponent, trước khi đăng nhập: redirect OAuth quay về origin nên mất query string,
     * phải giữ `?nhom=` (app kia gửi sang) vào sessionStorage.
     */
    captureRequestedGroup() {
        const match = /[?&]nhom=([^&#]*)/i.exec(window.location.search || '');
        if (match && match[1]) {
            sessionStorage.setItem(this._requestKey, decodeURIComponent(match[1]).trim().toUpperCase());
        }
    }

    /** Lấy nhóm app kia yêu cầu mở (chỉ dùng một lần). */
    consumeRequestedGroup(): string {
        const code = sessionStorage.getItem(this._requestKey);
        if (code) {
            sessionStorage.removeItem(this._requestKey);
        }
        return code;
    }

    getCachedGroup(): string {
        return sessionStorage.getItem(this._selectedKey);
    }

    select(code: string) {
        if (!code) {
            return;
        }
        sessionStorage.setItem(this._selectedKey, code);
        if (this.selectedCode$.value !== code) {
            this.selectedCode$.next(code);
        }
    }

    /** Gọi sau khi đã nạp quyền (fetchAuthorization). */
    load(): Promise<MenuPhanHe[]> {
        return this._htMenuService.getsNhomPhanHe().then(rs => {
            const data = rs && rs.status && rs.data ? rs.data : [];
            const phanHes = this.buildPhanHes(data);
            this.phanHes$.next(phanHes);
            return phanHes;
        }, () => {
            this.phanHes$.next([]);
            return [];
        });
    }

    getCurrentPhanHe(): MenuPhanHe {
        const phanHes = this.phanHes$.value || [];
        return phanHes.find(x => x.laAppHienTai) || null;
    }

    /** Mã nhóm của một menu cấp 1 trong app hiện tại; mã không còn hoạt động → "Khác". */
    getMenuGroupCode(item: any): string {
        const current = this.getCurrentPhanHe();
        const code = item && item.maNhomPhanHe ? item.maNhomPhanHe.toString().trim().toUpperCase() : null;
        if (!code || !current || current.maNhoms.indexOf(code) < 0) {
            return MA_NHOM_KHAC;
        }
        return code;
    }

    buildGroupUrl(phanHe: MenuPhanHe, nhom: MenuNhom): string {
        return phanHe.url + '/?nhom=' + encodeURIComponent(nhom.ma);
    }

    /** Cùng cách kiểm tra quyền với directive [authorize] + isNavigationPermission trên sidebar. */
    isGranted(phanQuyen: any): boolean {
        const user = this._userService.getBasicUserInfo();
        if (user && user.issuperuser) {
            return true;
        }

        let required = phanQuyen;
        if (required) {
            try {
                required = JSON.parse(required.toString().replace(/'/g, '"'));
            } catch (ex) {
                // directive cũng giữ nguyên chuỗi không parse được
            }
        }

        if (required instanceof Array) {
            return required.some(item => this._authorizeService.validated(item, PermissionTypes.PAGE));
        }
        return this._authorizeService.validated(required, PermissionTypes.PAGE);
    }

    private buildPhanHes(data: any[]): MenuPhanHe[] {
        const urls: any = environment.clientDomain.phanHeUrls || {};
        return (data || [])
            .map(item => {
                const laAppHienTai = item.phanHe === this._idPhanHe;
                const url = laAppHienTai ? window.location.origin : (urls[item.phanHe] || '');
                const nhoms: MenuNhom[] = (item.nhoms || [])
                    .filter(nhom => (nhom.phanQuyens || []).some(pq => this.isGranted(pq)));
                return <MenuPhanHe>{
                    phanHe: item.phanHe,
                    tenPhanHe: item.tenPhanHe,
                    url: url ? url.replace(/\/+$/, '') : '',
                    laAppHienTai: laAppHienTai,
                    maNhoms: (item.nhoms || []).map(nhom => nhom.ma).filter(ma => ma !== MA_NHOM_KHAC),
                    nhoms: nhoms
                };
            })
            .filter(item => item.laAppHienTai || (item.url && item.nhoms.length));
    }
}
